import React, { useState, useEffect } from "react";
import CheckoutButton from "../checkout/CheckoutButton";
import LoadingOverlay from "../utils/LoadingOverlay";
import { useNavigate } from 'react-router-dom';
import { 
    X, Unlock, Check, ShieldCheck, Star, CreditCard, 
    Lock, Lightbulb, Copy, UploadCloud, PackageCheck, 
    Zap, ArrowRight, CheckCircle2, BellRing 
} from 'lucide-react';

// =====================================================================
// 1. COMPONENTE: MODAL DE CUPOS AGOTADOS (SOLD OUT)
// =====================================================================
const SoldOutModalStyles = () => (
    <style>{`
.soldout-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.85); display: flex; justify-content: center;
  align-items: center; z-index: 2000; opacity: 0; animation: fadeIn 0.3s forwards;
  padding: 1rem;
  backdrop-filter: blur(5px);
}
.soldout-modal {
  background: #1f2937; color: white; padding: 3rem 2rem; border-radius: 16px;
  width: 90%; max-width: 500px; text-align: center;
  border: 1px solid #ef4444;
  box-shadow: 0 15px 40px rgba(239, 68, 68, 0.15);
  transform: scale(0.95); animation: zoomIn 0.3s 0.1s forwards; position: relative;
}
.soldout-modal .close-button {
  position: absolute; top: 15px; right: 15px; background: none; border: none;
  color: #9ca3af; cursor: pointer; transition: color 0.2s; padding: 5px;
}
.soldout-modal .close-button:hover { color: white; }
.soldout-modal .icon-wrapper {
  margin: 0 auto 1.5rem; width: 80px; height: 80px;
  background-color: rgba(239, 68, 68, 0.1);
  border-radius: 50%; display: flex; justify-content: center; align-items: center;
  border: 2px solid rgba(239, 68, 68, 0.2);
}
.soldout-modal .icon-wrapper svg { width: 40px; height: 40px; color: #ef4444; }
.soldout-modal .title { font-size: 2rem; font-weight: 800; margin-bottom: 1rem; color: #ffffff; }
.soldout-modal .subtitle { color: #ef4444; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; font-size: 0.85rem; margin-bottom: 0.5rem; }
.soldout-modal .description { color: #d1d5db; font-size: 1.05rem; line-height: 1.6; margin-bottom: 2rem; }
.soldout-modal .actions { display: flex; flex-direction: column; gap: 1rem; }
.soldout-modal .btn-waitlist {
  border: none; padding: 1rem; font-size: 1rem; font-weight: 700;
  border-radius: 8px; cursor: pointer; transition: all 0.2s ease-in-out;
  background-image: linear-gradient(to right, #ea580c, #ef4444); color: white; text-transform: uppercase;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
}
.soldout-modal .btn-waitlist:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(239, 68, 68, 0.3); }
.soldout-modal .btn-close-action { 
  background-color: transparent; border: 2px solid #374151; color: #9ca3af; padding: 0.8rem; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s;
}
.soldout-modal .btn-close-action:hover { border-color: #6b7280; color: white; background-color: rgba(255,255,255,0.05); }
@keyframes fadeIn { to { opacity: 1; } }
@keyframes zoomIn { to { transform: scale(1); } }
`}</style>
);

const SoldOutModal = ({ onClose, course }) => {
    // Función mock para lista de espera (puedes conectarla a tu backend o whatsapp)
    const handleJoinWaitlist = () => {
        const phoneNumber = "573014953565";
        const message = encodeURIComponent(`Hola me interesa el ${course.typeTitle} "${course.titleMain}" y quisiera estar en lista de espera por favor`);
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
        window.open(whatsappUrl, "_blank");
        onClose();
    };

    return (
        <>
            <SoldOutModalStyles />
            <div className="soldout-overlay">
                <div className="soldout-modal">
                    <button className="close-button" onClick={onClose}><X size={24} /></button>
                    <div className="icon-wrapper"><Lock /></div>
                    <p className="subtitle">Inscripciones Cerradas</p>
                    <h2 className="title">¡Cupos Agotados!</h2>
                    <p className="description">
                        Lo sentimos, hemos alcanzado el límite máximo de estudiantes. 
                        <strong>No es posible realizar nuevas inscripciones en este momento.</strong>
                    </p>
                    <div className="actions">
                        <button className="btn-waitlist" onClick={handleJoinWaitlist}>
                            <BellRing size={20} /> Avísenme cuando abran cupos
                        </button>
                        <button className="btn-close-action" onClick={onClose}>Entendido, volver al inicio</button>
                    </div>
                </div>
            </div>
        </>
    );
};

// =====================================================================
// 3. COMPONENTE PRINCIPAL: INSCRIPTION MODAL (CONTROLADOR)
// =====================================================================
const InscriptionModal = ({ show, handleClose, course, newPrice, currency, country }) => {

    const navigate = useNavigate();
    
    // --- ESTADOS ---
    const [loading, setLoading] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState("full");
    const [paymentMethod, setPaymentMethod] = useState(""); 
    const [isCountryLocked, setIsCountryLocked] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "", email: "", phone: "", country: "", 
        paymentProof: null, nameHolder: "", emailHolder: "",
    });
    const [showPaymentDetails, setShowPaymentDetails] = useState(false);
    const [showPaymentProof, setShowPaymentProof] = useState(false);
    const [showPaymentInfo, setShowPaymentInfo] = useState("");
    const [copiedField, setCopiedField] = useState(null);

    // --- LÓGICA DE NEGOCIO ---
    const numericPrice = parseFloat(String(newPrice).replace(/\./g, '').replace(',', '.')) || 0;

    useEffect(() => {
        if (country) {
            const knownCountries = ["VE", "CO", "CL", "PE", "US", "MX", "ES", "AR"];
            const isKnown = knownCountries.includes(country.toUpperCase());
            const selectedCountry = isKnown ? country.toUpperCase() : "other";
            setIsCountryLocked(isKnown);
            setFormData((prev) => ({ ...prev, country: selectedCountry }));
            updatePaymentOptions(selectedCountry);
        }
    }, [country, show]);

    useEffect(() => {
    if (showPaymentInfo === "ve") {
        setPaymentMethod("transfer");
    }
    else if (showPaymentInfo === "co") {
        setPaymentMethod();
    }
    else if (showPaymentInfo !== "co") {
        setPaymentMethod("gateway");
    }
}, [showPaymentInfo]);

    const updatePaymentOptions = (selectedCountry) => {
        setShowPaymentProof(selectedCountry === "CO" || selectedCountry === "VE");
        if (selectedCountry === "VE") setShowPaymentInfo("ve");
        else if (selectedCountry === "CO") setShowPaymentInfo("co");
        else setShowPaymentInfo(selectedCountry);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePaymentMethodChange = (method) => {
        setPaymentMethod(method);
        if (method === 'gateway') setShowPaymentProof(false);
        else setShowPaymentProof(true);
    };

    const handleFileChange = (e) => {
        setFormData((prev) => ({ ...prev, paymentProof: e.target.files[0] }));
    };

    const handleCountryChange = (e) => {
        const selectedVal = e.target.value;
        setFormData((prev) => ({ ...prev, country: selectedVal }));
        setShowPaymentDetails(selectedVal !== "" && selectedVal !== "VE");
        setShowPaymentProof(selectedVal === "CO" || selectedVal === "VE");
        if (selectedVal === "VE") setShowPaymentInfo("ve");
        else if (selectedVal === "CO") setShowPaymentInfo("co");
        else setShowPaymentInfo(selectedVal);
    };

    const copyToClipboard = (text, fieldId) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                setCopiedField(fieldId);
                setTimeout(() => setCopiedField(null), 2000);
            }).catch(err => console.error('Error al copiar: ', err));
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "fixed";
            textArea.style.left = "-9999px";
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedField(fieldId);
                setTimeout(() => setCopiedField(null), 2000);
            } catch (err) { console.error('Error al copiar', err); }
            document.body.removeChild(textArea);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        localStorage.setItem("courseData", JSON.stringify({
            student: formData,
            course_name: course.titleMain,
            course_type_title: course.typeTitle,
            payment_method: paymentMethod,
            course_price: newPrice,
            course_currency: currency,
            course_id: course.id,
            send_inscription: true
        }));

        try {
            const formDataToSend = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                formDataToSend.append(key, value);
            });

            formDataToSend.append('course_id', course.id);
            formDataToSend.append('course_name', course.titleMain);
            formDataToSend.append('course_type_title', course.typeTitle);
            formDataToSend.append('payment_method', paymentMethod);
            formDataToSend.append('program_price', newPrice);
            formDataToSend.append('currency', currency);

            try{
                const apiUrlSaveStudent = `${process.env.REACT_APP_HOST_BACKEND}/students/insert`;
                const response = await fetch(apiUrlSaveStudent, {
                    method: "POST",
                    body: formDataToSend,
                    mode: "cors"
                });

                if (!response.ok) {
                    throw new Error("Error al insertar estudiante");
                }

                const result = await response.json();
                console.log("Respuesta backend:", result);

                // 🔐 AQUÍ tienes todo
                const { student_id, program_id, payment_token } = result;

                // 👉 Guardar token para el correo
                formDataToSend.append("payment_token", payment_token);
            } catch (error) {
                alert("Hubo un problema al guardar el estudiante en el sistema.");
            }

            const apiUrl = `${process.env.REACT_APP_HOST_BACKEND}/send_inscription_academy`;
            const response = await fetch(apiUrl, {
                method: "POST",
                body: formDataToSend,
                mode: 'cors'
            });

            const result = await response.json();
            handleClose();
            if (result.status === "success") navigate('/success');
            else navigate('/rejected');
        } catch (error) {
            alert("Hubo un problema al enviar la inscripción.");
        } finally {
            setLoading(false);
        }
    };

    // --- VARIABLES DE CÁLCULO ---
    const isCardPayment = (showPaymentInfo !== "ve" && showPaymentInfo !== "co" && showPaymentInfo !== "") || (showPaymentInfo === "co" && paymentMethod === "gateway");
    const isFormComplete = formData.fullName && formData.email && formData.phone && formData.country &&
        (!showPaymentProof || formData.paymentProof) &&
        (!isCardPayment && (showPaymentProof) || (formData.nameHolder && formData.emailHolder));
    const calculatedPrice = paymentAmount === "full" ? numericPrice : Math.round(numericPrice * 0.6);
    const totalCost = `${calculatedPrice.toLocaleString("de-DE")} ${currency}`;
    const items = [{
        id: course?.id,
        title: course?.titleMain || "Programa Online",
        price: calculatedPrice,
        quantity: 1,
        currency: currency === "$" ? "USD" : currency,
        country: country,
        typeTitle: course?.typeTitle,
        type: course?.type,
        paymentMethod: paymentMethod
    }];

    // =====================================================================
    // 4. RENDERIZADO CONDICIONAL (AQUÍ ESTÁ LA MAGIA)
    // =====================================================================
    
    // Si el modal no debe mostrarse, retornamos null
    if (!show) return null;

    // VALIDACIÓN DE CUPOS: Si openRegistrations es false, mostramos el modal de Agotado
    // Usamos course?.openRegistrations para evitar errores si course es null
    if (course && course.openRegistrations === false) {
        return <SoldOutModal onClose={handleClose} course={course} />;
    }

    // SI HAY CUPOS (openRegistrations === true), mostramos el Checkout Normal
    return (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md overflow-y-auto animate-fade-in">
            <div className="flex min-h-full items-start justify-center p-0 sm:p-4 sm:items-center">
                <div className="bg-[#0f0f0f] w-full max-w-2xl border border-gray-800 shadow-2xl relative flex flex-col my-0 sm:rounded-2xl sm:my-8 transition-all">
                    
                    {/* Botón Cerrar */}
                    <button onClick={handleClose} className="absolute top-4 right-4 z-20 text-gray-400 hover:text-white bg-black/50 p-2 rounded-full hover:bg-gray-800 transition-colors">
                        <X className="w-6 h-6" />
                    </button>

                    {/* Contenido */}
                    <div className="p-6 md:p-8">
                        {/* ENCABEZADO */}
                        <div className="text-center mb-6 mt-12 sm:mt-0">
                            <h2 className="text-2xl md:text-3xl font-extrabold text-white leading-tight mb-2 font-montserrat">
                                Estás a un paso de empezar tu formación
                            </h2>
                            <p className="text-[#ccff00] font-semibold text-lg flex items-center justify-center gap-2">
                                <Unlock className="w-5 h-5" />
                                {course.configCheckout?.hook || "Acceso inmediato al curso completo"}
                            </p>
                            <h5 className="text-lg font-semibold text-white leading-tight mb-2 font-montserrat">
                                {course.configCheckout?.topHook}
                            </h5>
                        </div>

                        {/* TARJETA BLANCA */}
                        <div className="bg-gray-50 rounded-xl p-6 md:p-8 shadow-xl border border-gray-200 relative">
                            {/* --- LOADING OVERLAY --- */}
                            {loading && <LoadingOverlay />}
                            {/* ----------------------- 
                            
                            {loading && (
                                <div className="absolute inset-0 bg-white/80 z-50 flex items-center justify-center rounded-xl">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ccff00]"></div>
                                </div>
                            )}*/}

                            {/* PRECIO */}
                            <div className="text-center mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-1">Inversión total hoy</p>
                                <span className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight block">
                                    {totalCost}
                                </span>
                                <div className="text-sm text-gray-500 font-medium mt-1">Pago único · Sin cargos ocultos</div>
                            </div>

                            {/* TESTIMONIOS */}
                            {course.configCheckout?.testimony1 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                                    {[1, 2].map((i) => (
                                        <div key={i} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                            <div className="flex text-yellow-400 mb-2">
                                                {[...Array(5)].map((_, idx) => <Star key={idx} className="w-3 h-3 fill-current" />)}
                                            </div>
                                            <p className="text-xs text-gray-600 italic">
                                                {i === 1 ? `“${course.configCheckout.testimony1}”` : `“${course.configCheckout.testimony2}”`}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* QUÉ INCLUYE */}
                            <div className="mb-2 border-t border-b border-gray-200 py-6">
                                <h3 className="text-gray-900 font-extrabold text-xl mb-4 flex items-center gap-2">
                                    <PackageCheck className="w-6 h-6 text-lime-600" />
                                    ¿Qué incluye tu inscripción?
                                </h3>
                                <ul className="space-y-3">
                                    {course.configCheckout?.includedCourse?.map((line, i) => (
                                        <li key={i} className="flex items-start gap-3">
                                            <div className="mt-0.5"><Check className="text-white bg-lime-500 rounded-full p-0.5 w-4 h-4 stroke-[3]" /></div>
                                            <span className="text-sm font-medium text-gray-700">{line}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* FORMULARIO */}
                            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
                                <div className="space-y-4">
                                    <h3 className="text-gray-900 font-bold text-lg mb-2 border-b border-gray-200 pb-2">1. Tus datos de inscripción</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-gray-600 text-xs uppercase font-bold mb-1 ml-1">Nombre Completo</label>
                                            <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Ej. Juan Pérez" className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-all shadow-sm" required />
                                        </div>
                                        <div>
                                            <label className="block text-gray-600 text-xs uppercase font-bold mb-1 ml-1">Correo Electrónico</label>
                                            <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Ej. juan@correo.com" className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-all shadow-sm" required />
                                        </div>
                                        <div>
                                            <label className="block text-gray-600 text-xs uppercase font-bold mb-1 ml-1">Teléfono (WhatsApp)</label>
                                            <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+57 ..." className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-all shadow-sm" required />
                                        </div>
                                        <div>
                                            <label className="block text-gray-600 text-xs uppercase font-bold mb-1 ml-1">País de Inscripción</label>
                                            <select name="country" value={formData.country} onChange={handleCountryChange} disabled={isCountryLocked} className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 focus:outline-none focus:border-lime-500 focus:ring-1 focus:ring-lime-500 transition-all shadow-sm appearance-none" required>
                                                <option value="">Selecciona un país</option>
                                                <option value="VE">Venezuela</option>
                                                <option value="CO">Colombia</option>
                                                <option value="CL">Chile</option>
                                                <option value="PE">Perú</option>
                                                <option value="US">Estados Unidos</option>
                                                <option value="MX">México</option>
                                                <option value="ES">España</option>
                                                <option value="AR">Argentina</option>
                                                <option value="other">Otro</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* LÓGICA DE PAGO */}
                                <div className="pt-4">
                                    <h3 className="text-gray-900 font-bold text-lg mb-4 border-b border-gray-200 pb-2">2. Método de pago seguro</h3>
                                    
                                    {/* COLOMBIA */}
                                    {showPaymentInfo === "co" && (
                                        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                                            <strong className="block mb-3 text-blue-900 text-sm">Elige tu método de pago:</strong>
                                            <div className="flex gap-3">
                                                <button type="button" onClick={() => handlePaymentMethodChange("transfer")} className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold border transition-all ${paymentMethod === "transfer" ? "bg-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"}`}>Transferencia</button>
                                                <button type="button" onClick={() => handlePaymentMethodChange("gateway")} className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold border transition-all ${paymentMethod === "gateway" ? "bg-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"}`}>Tarjeta (crédito/débito) o PSE</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* VENEZUELA */}
                                    {showPaymentInfo === "ve" && (
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4 animate-fade-in">
                                            <div className="text-center mb-2"><div className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full inline-block mb-0">Datos del Pago Móvil</div></div>
                                            <div className="text-center mb-1"><p className="text-sm text-gray-600 font-bold">⏱ Proceso rápido: sube tu comprobante y recibe acceso en maximo 24horas</p></div>
                                            
                                            {/* Datos VE */}
                                            <div className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                                                <div><span className="text-xs text-gray-500 block uppercase font-bold">Banco</span><span className="font-mono font-bold text-gray-800">Provincial</span></div>
                                                <button type="button" onClick={() => copyToClipboard("0108", "banco")} className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${copiedField === "banco" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{copiedField === "banco" ? <Check size={14} /> : <Copy size={14} />}{copiedField === "banco" ? "Copiado" : "Copiar"}</button>
                                            </div>
                                            <div className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                                                <div><span className="text-xs text-gray-500 block uppercase font-bold">Cédula</span><span className="font-mono font-bold text-gray-800">19241582</span></div>
                                                <button type="button" onClick={() => copyToClipboard("19241582", "cedula")} className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${copiedField === "cedula" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{copiedField === "cedula" ? <Check size={14} /> : <Copy size={14} />}{copiedField === "cedula" ? "Copiado" : "Copiar"}</button>
                                            </div>
                                            <div className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                                                <div><span className="text-xs text-gray-500 block uppercase font-bold">Celular</span><span className="font-mono font-bold text-gray-800">04163574963</span></div>
                                                <button type="button" onClick={() => copyToClipboard("04163574963", "celular")} className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${copiedField === "celular" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{copiedField === "celular" ? <Check size={14} /> : <Copy size={14} />}{copiedField === "celular" ? "Copiado" : "Copiar"}</button>
                                            </div>
                                            {/* Upload VE */}
                                            <div>
                                                <label className="block text-gray-600 text-xs uppercase font-bold mb-1">Subir Comprobante</label>
                                                <div className="relative">
                                                    <input type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" required />
                                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50 transition-colors bg-white">
                                                        <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                        <span className="text-sm text-gray-500">{formData.paymentProof ? formData.paymentProof.name : "Clic para subir imagen (JPG/PNG)"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* COLOMBIA TRANSFERENCIA */}
                                    {showPaymentInfo === "co" && paymentMethod === "transfer" && (
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4 animate-fade-in">
                                            <div className="text-center mb-2"><div className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full inline-block mb-0">Datos de Transferencia</div></div>
                                            <div className="text-center mb-1"><p className="text-sm text-gray-600 font-bold">⏱ Proceso rápido: sube tu comprobante y recibe acceso en maximo 24horas</p></div>
                                            {/* Datos Bancolombia */}
                                            <div className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                                                <div><span className="text-xs text-gray-500 block uppercase font-bold">Bancolombia Ahorros</span><span className="font-mono font-bold text-gray-800">00689258018</span></div>
                                                <button type="button" onClick={() => copyToClipboard("00689258018", "bancolombia")} className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${copiedField === "bancolombia" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{copiedField === "bancolombia" ? <Check size={14} /> : <Copy size={14} />}{copiedField === "bancolombia" ? "Copiado" : "Copiar"}</button>
                                            </div>
                                            {/* Datos Nequi */}
                                            <div className="bg-white p-3 rounded border border-gray-200 flex items-center justify-between">
                                                <div><span className="text-xs text-gray-500 block uppercase font-bold">Nequi</span><span className="font-mono font-bold text-gray-800">3177169274</span></div>
                                                <button type="button" onClick={() => copyToClipboard("3177169274", "nequi")} className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${copiedField === "nequi" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{copiedField === "nequi" ? <Check size={14} /> : <Copy size={14} />}{copiedField === "nequi" ? "Copiado" : "Copiar"}</button>
                                            </div>
                                            {/* Upload CO */}
                                            <div>
                                                <label className="block text-gray-600 text-xs uppercase font-bold mb-1">Subir Comprobante de pago</label>
                                                <div className="relative">
                                                    <input type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" required />
                                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50 transition-colors bg-white">
                                                        <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                                        <span className="text-sm text-gray-500">{formData.paymentProof ? formData.paymentProof.name : "Clic para subir imagen (JPG/PNG)"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* PASARELA DE PAGO */}
                                    {isCardPayment && (
                                        <div className="space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-100 animate-fade-in">
                                            <h4 className="text-sm font-bold text-blue-900 mb-2">Datos del Titular de la Tarjeta</h4>
                                            <div>
                                                <label className="block text-gray-600 text-xs uppercase font-bold mb-1">Nombre del Pagador</label>
                                                <input type="text" name="nameHolder" placeholder="Como aparece en la tarjeta" onChange={handleInputChange} className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 text-sm focus:outline-none focus:border-blue-500 shadow-sm" required />
                                            </div>
                                            <div>
                                                <label className="block text-gray-600 text-xs uppercase font-bold mb-1">Correo del Pagador</label>
                                                <input type="email" name="emailHolder" onChange={handleInputChange} className="w-full bg-white border border-gray-300 rounded-lg p-3 text-gray-900 text-sm focus:outline-none focus:border-blue-500 shadow-sm" required />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* BOTONES FINALES */}
                                <div className="pt-6 border-t border-gray-200 space-y-4">
                                    <div className="text-center space-y-1">
                                        <p className="text-gray-700 text-sm font-medium">{course.configCheckout?.bottomHook}</p>
                                    </div>

                                    {isCardPayment ? (
                                        <div className="w-full">
                                            <CheckoutButton items={items} formData={formData} disabled={!isFormComplete} type={1} />
                                        </div>
                                    ) : (
                                        <button type="button" onClick={handleSubmit} disabled={!isFormComplete || loading} className={`w-full bg-[#ccff00] hover:bg-[#b3e600] text-black font-black text-xl py-4 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.01] flex items-center justify-center gap-2 border border-lime-400 ${(!isFormComplete || loading) ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}>
                                            {loading ? <span>Enviando...</span> : <><span>Enviar inscripción ahora</span><ArrowRight className="w-6 h-6" /></>}
                                        </button>
                                    )}

                                    <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                                        <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Pago 100% seguro</span>
                                        <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Garantía incluida</span>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InscriptionModal;